import { useCallback, useMemo, useReducer, useState } from 'react';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';
import { useTracking } from '@frontend/tracking';
import type { AgentVersionSummary } from '@frontend/types';

import { initialState, reducer, type VersionToRestore } from './reducer';

const PAGE_SIZE = 20;

export type SelectVersionResult = {
  version: AgentVersionSummary | null;
  displayLabel: string | number;
  isCurrentVersion: boolean;
};

export type RestoreDialog = {
  isOpen: boolean;
  versionToRestore: VersionToRestore;
  onOpen: (version: AgentVersionSummary, displayLabel: string | number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
};

export type AgentVersionsConfig = {
  fetchVersions: (
    instanceId: string,
    agentId: string,
    limit: number,
    offset: number
  ) => () => Promise<{ versions: AgentVersionSummary[]; total_versions: number }>;
  fetchVersionsKey: (
    instanceId: string,
    agentId: string,
    limit: number,
    offset: number
  ) => readonly unknown[];
  fetchVersion: (url: string) => () => Promise<unknown>;
  fetchVersionKey: (url: string) => readonly unknown[];
  duplicateRoute: (agentId: string) => string;
  versionsCachePrefix: string;
  restoreFn: (agentUrl: string, fullAgent: unknown) => Promise<unknown>;
  onRestoreSuccess: (queryClient: ReturnType<typeof useQueryClient>, agentId: string) => void;
  onRestoreError?: (error: unknown, setRestoreError: (errors: string[] | null) => void) => boolean;
};

export function useAgentVersionsBase(agentId: string, config: AgentVersionsConfig) {
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  const navigate = useNamespacedNavigate();
  const { track } = useTracking();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;
  const [restoreError, setRestoreError] = useState<string[] | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: config.fetchVersionsKey(instanceId ?? '', agentId, PAGE_SIZE, offset),
    queryFn: config.fetchVersions(instanceId ?? '', agentId, PAGE_SIZE, offset),
    enabled: !!instanceId && !!agentId,
  });

  const versions = useMemo(() => data?.versions ?? [], [data?.versions]);
  const totalVersions = data?.total_versions ?? 0;
  const totalPages = Math.ceil(totalVersions / PAGE_SIZE);

  const versionUrl = state.selection?.versionSummary?.links?.self ?? '';
  const { data: selectedVersionData } = useQuery({
    queryKey: config.fetchVersionKey(versionUrl),
    queryFn: config.fetchVersion(versionUrl),
    enabled: !!versionUrl,
  });

  const selectedVersionDisplayLabel = useMemo(() => {
    if (!state.selection) return '';
    return `V${state.selection.versionNumber}`;
  }, [state.selection]);

  const { mutate: duplicateAgent } = useMutation({
    mutationFn: async ({ version }: { version: AgentVersionSummary }) => {
      if (!version.links?.self) {
        throw new Error('Missing version URL');
      }
      return config.fetchVersion(version.links.self)();
    },
    onSuccess: fullAgent => {
      track('Agent Version Duplicated as New Agent');
      navigate(config.duplicateRoute(agentId), {
        state: { agent: fullAgent },
      });
    },
    onError: () => {
      toaster.create('Failed to load agent version for duplication', { intent: 'danger' });
    },
  });

  const { mutate: restoreAgent, isPending: isRestoring } = useMutation({
    mutationFn: async ({
      version,
    }: {
      version: AgentVersionSummary;
      displayLabel: string | number;
    }) => {
      if (!version.links?.self || !version.links?.agent) {
        throw new Error('Missing version or agent URL');
      }
      const fullAgent = await config.fetchVersion(version.links.self)();
      return config.restoreFn(version.links.agent, fullAgent);
    },
    onSuccess: (_, { displayLabel }) => {
      config.onRestoreSuccess(queryClient, agentId);
      toaster.create(`Agent restored to ${displayLabel} successfully`, {
        intent: 'success',
      });
      track('Agent Version Restored');
      dispatch({ type: 'RESTORE_SUCCESS' });
    },
    onError: (error: unknown) => {
      const handled = config.onRestoreError?.(error, setRestoreError);
      if (!handled) {
        toaster.create('Failed to restore agent version', { intent: 'danger' });
      }
      dispatch({ type: 'CLOSE_RESTORE_DIALOG' });
    },
  });

  const resetSelection = useCallback(() => {
    dispatch({ type: 'RESET_SELECTION' });
  }, []);

  const handleDuplicate = useCallback(
    (version: AgentVersionSummary) => {
      duplicateAgent({ version });
    },
    [duplicateAgent]
  );

  const selectVersion = useCallback(
    (version: AgentVersionSummary): SelectVersionResult => {
      const isCurrentVersion =
        versions.length > 0 && version.internal_version === versions[0].internal_version;

      dispatch({
        type: 'SELECT_VERSION',
        versionNumber: version.internal_version,
        versionSummary: isCurrentVersion ? null : version,
      });

      return {
        version: isCurrentVersion ? null : version,
        displayLabel: version.internal_version,
        isCurrentVersion,
      };
    },
    [versions]
  );

  const restoreDialog: RestoreDialog = useMemo(
    () => ({
      isOpen: state.restoreDialog.isOpen,
      versionToRestore: state.restoreDialog.versionToRestore,
      onOpen: (version: AgentVersionSummary, displayLabel: string | number) => {
        dispatch({ type: 'OPEN_RESTORE_DIALOG', version, displayLabel });
      },
      onConfirm: () => {
        if (!state.restoreDialog.versionToRestore) return;
        restoreAgent({
          version: state.restoreDialog.versionToRestore.version,
          displayLabel: state.restoreDialog.versionToRestore.displayLabel,
        });
      },
      onCancel: () => dispatch({ type: 'CLOSE_RESTORE_DIALOG' }),
      isLoading: isRestoring,
    }),
    [state.restoreDialog, isRestoring, restoreAgent]
  );

  return {
    versions,
    isLoading,
    error,
    selectedVersionSummary: state.selection?.versionSummary ?? null,
    selectedVersionData,
    selectedVersionDisplayLabel,
    selectVersion,
    resetSelection,
    handleDuplicate,
    restoreDialog,
    restoreError,
    clearRestoreError: useCallback(() => setRestoreError(null), []),
    page,
    setPage,
    totalPages,
    totalVersions,
  };
}
